import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { isNative, isIOS, hasNativePlugin } from './is-native';

/**
 * JS bridge for `MuckaTapToPayPlugin` (ios/App/App/MuckaTapToPayPlugin.swift).
 *
 * Tap to Pay on iPhone turns the engineer's own phone into the card reader.
 * This side owns two things the native side cannot do for itself:
 *
 * 1. **Authentication.** Stripe's SDK asks for a connection token on a
 *    background thread and blocks on the answer. Only the WebView holds the
 *    user's Supabase session, so native raises `connectionTokenRequested` and
 *    we answer it. See the comment on `MuckaConnectionTokenProvider` for why a
 *    native fetch is the wrong shape.
 *
 * 2. **Who may accept Apple's terms.** Apple requirement 3.8 says only an
 *    administrator may accept the Terms and Conditions, so `connect` carries a
 *    `tosAcceptancePermitted` flag derived from `manage_workspace`. Passed
 *    false, Stripe never raises the terms sheet.
 *
 * ⚠️ Nothing here caches whether terms are accepted. Requirement 1.6 says that
 * is read from Apple every time, which `getStatus()` does. Do not memoise it.
 *
 * On web and on Android every method is a graceful no-op returning
 * `supported: false`, so callers can call unconditionally and fall back to the
 * QR flow, which in the UK is a required fallback rather than a nicety
 * (requirement 4.8).
 */

export type TapToPayUnsupportedReason = 'os_version' | 'not_native' | 'platform';

export interface TapToPaySupport {
  supported: boolean;
  reason?: TapToPayUnsupportedReason | null;
  /** e.g. "16.4" — for the "update your iPhone" message (requirement 1.4). */
  minimumVersion?: string;
}

export interface TapToPayStatus {
  supported: boolean;
  /** Read live from Apple. Never stored. */
  termsAccepted: boolean;
  connected: boolean;
}

export interface TapToPayConnectOptions {
  locationId: string;
  /** Derived from `manage_workspace`. Apple requirement 3.8. */
  tosAcceptancePermitted: boolean;
  merchantDisplayName?: string;
}

/** What the connection-token route hands back. */
export interface TerminalSession {
  connectionToken: string;
  locationId: string;
  connectedAccountId: string;
}

export type TapToPayIntent = 'setup' | 'charge';

export interface TapToPayResult {
  paymentIntentId: string;
  status: 'succeeded';
}

/**
 * Apple require distinct screens for these (5.7 "Initializing", 5.8
 * "Processing"), and review for them. `processing` is the one people forget:
 * the card has been read and the customer can take it away, so leaving
 * "Present card" on screen is both wrong and slower-feeling than it is.
 */
export type TapToPayStage = 'collecting' | 'processing' | 'done';

interface MuckaTapToPayPlugin {
  isSupported(): Promise<TapToPaySupport>;
  getStatus(): Promise<TapToPayStatus>;
  connect(options: TapToPayConnectOptions): Promise<{ connected: boolean }>;
  disconnect(): Promise<void>;
  collectPayment(options: { clientSecret: string }): Promise<TapToPayResult>;
  cancelPayment(): Promise<{ cancelled: boolean }>;
  showEducation(): Promise<{ shown: boolean }>;
  provideConnectionToken(options: {
    requestId: string;
    token?: string;
    error?: string;
  }): Promise<void>;
  addListener(
    eventName: 'connectionTokenRequested',
    listenerFunc: (data: { requestId: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'updateProgress',
    listenerFunc: (data: { progress: number; installing: boolean; error?: string | null }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'displayMessage' | 'readerInput',
    listenerFunc: (data: { message: string; code: number }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'termsAccepted',
    listenerFunc: () => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: 'paymentStage',
    listenerFunc: (data: { stage: TapToPayStage }) => void,
  ): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

const MuckaTapToPay = registerPlugin<MuckaTapToPayPlugin>('MuckaTapToPay');

/**
 * iOS only for now. Android is a separate build and is currently paused.
 *
 * `hasNativePlugin` is load-bearing rather than defensive: we ship the WebView
 * hosted, so this file reaches every phone the moment it deploys, while the
 * plugin only arrives with a store update. Without the check, every user on
 * yesterday's binary hits a "not implemented" rejection instead of quietly
 * falling back to the QR code.
 */
function available(): boolean {
  return isNative() && isIOS() && hasNativePlugin('MuckaTapToPay');
}

/**
 * Could this device possibly tap? Synchronous, so a caller can decide what to
 * paint on the FIRST frame.
 *
 * `isTapToPaySupported()` is the real answer but it is async, and an effect
 * runs after the browser paints — so a sheet that waits for it shows one frame
 * of nothing to everyone on web and Android, where the answer was never in
 * doubt. This is only the cheap half of the check (native + iOS + the plugin is
 * in the binary); the OS-version floor still needs the async call.
 */
export function isTapToPayPossible(): boolean {
  return available();
}

export async function isTapToPaySupported(): Promise<TapToPaySupport> {
  if (!available()) return { supported: false, reason: isNative() ? 'platform' : 'not_native' };
  try {
    return await MuckaTapToPay.isSupported();
  } catch {
    // A plugin that isn't in the binary rejects with "not implemented". That
    // is a build problem, not a device problem, but from the engineer's side
    // the answer is the same: use the QR code.
    return { supported: false, reason: 'platform' };
  }
}

/**
 * Has this device ever been through Tap to Pay? Set the first time a status
 * read comes back with terms accepted, and never cleared.
 *
 * 🚨 This is NOT whether terms are accepted, and must never be read as that.
 * Requirement 1.6 says acceptance is asked of Apple every time, and it still
 * is: this only decides whether the launch warm-up asks at all. It exists
 * because asking is not free. `getStatus` has to start Stripe's Terminal SDK
 * before it can reach Apple, and that start runs on the main thread and writes
 * to SQLite. On every launch, for every iOS user, it froze the app for 5–6
 * seconds on a memory-starved device (MUCKA-IOS-S), most of them people who
 * will never take a card payment.
 *
 * Cost of the gate: a phone that has never opened the payment sheet (or
 * Settings → Payments) pays the cold connect once, the first time it does.
 * Every launch after that is warm, as before.
 */
export const TAP_TO_PAY_USED_KEY = 'mucka.tapToPay.used';

function hasUsedTapToPay(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(TAP_TO_PAY_USED_KEY) === '1';
  } catch {
    return false;
  }
}

function markTapToPayUsed(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(TAP_TO_PAY_USED_KEY, '1');
  } catch {
    // Private mode or a full quota. The warm-up just stays off; nothing breaks.
  }
}

export async function getTapToPayStatus(): Promise<TapToPayStatus> {
  if (!available()) return { supported: false, termsAccepted: false, connected: false };
  try {
    const status = await MuckaTapToPay.getStatus();
    if (status.termsAccepted) markTapToPayUsed();
    return status;
  } catch {
    return { supported: false, termsAccepted: false, connected: false };
  }
}

/**
 * Ask the server for a Terminal session.
 *
 * `intent` decides the permission the route requires: `setup` needs
 * `manage_workspace` because it is the connect that raises Apple's terms sheet,
 * `charge` needs `collect_payment`. Kept in step with
 * `src/lib/payments/tap-to-pay.ts`.
 */
export async function fetchTerminalSession(intent: TapToPayIntent): Promise<TerminalSession> {
  const res = await fetch('/api/terminal/connection-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ intent }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    // The 403 body carries Apple's 3.8.1 "contact an admin" sentence, and the
    // 409 body carries "this workspace hasn't finished connecting Stripe".
    // Both are meant to be shown, so keep the message rather than replacing it.
    throw new Error(body?.error || 'Could not start a card payment session.');
  }
  return body as TerminalSession;
}

let tokenListener: PluginListenerHandle | null = null;
/**
 * The token minted alongside the locationId, held so the first
 * `connectionTokenRequested` after a connect doesn't cost a second round trip.
 * Consumed once; every later request fetches fresh.
 */
let primedToken: { token: string; intent: TapToPayIntent } | null = null;

/**
 * Wire native's token requests to our API. Idempotent, and safe to call on
 * every app resume.
 */
export async function startTapToPayTokenBridge(): Promise<void> {
  if (!available() || tokenListener) return;
  try {
    tokenListener = await MuckaTapToPay.addListener(
      'connectionTokenRequested',
      async ({ requestId }) => {
        try {
          const primed = primedToken;
          primedToken = null;
          const token = primed
            ? primed.token
            : (await fetchTerminalSession('charge')).connectionToken;
          await MuckaTapToPay.provideConnectionToken({ requestId, token });
        } catch (e) {
          // Native is blocking on this. It MUST be answered either way, or the
          // SDK sits on an unfulfilled completion block until its own timeout.
          await MuckaTapToPay.provideConnectionToken({
            requestId,
            error: e instanceof Error ? e.message : 'Could not start a card payment session.',
          }).catch(() => {});
        }
      },
    );
  } catch {
    tokenListener = null;
  }
}

export async function stopTapToPayTokenBridge(): Promise<void> {
  await tokenListener?.remove().catch(() => {});
  tokenListener = null;
  primedToken = null;
}

/**
 * Connect this phone as a reader.
 *
 * `canAcceptTerms` should come from the user's `manage_workspace` permission.
 * It is passed straight to the SDK, which will refuse to show Apple's terms
 * sheet without it, so a false here is a real gate and not a UI hint.
 *
 * Also the warm-up path for Apple requirement 1.5: called on resume when terms
 * are already accepted, so that the reader UI can appear inside the one second
 * requirement 5.6 asks for.
 */
export async function connectTapToPay(opts: {
  canAcceptTerms: boolean;
  merchantDisplayName?: string;
}): Promise<{ connected: boolean }> {
  if (!available()) return { connected: false };

  await startTapToPayTokenBridge();

  const status = await getTapToPayStatus();
  const intent: TapToPayIntent = status.termsAccepted ? 'charge' : 'setup';
  const session = await fetchTerminalSession(intent);
  primedToken = { token: session.connectionToken, intent };

  try {
    return await MuckaTapToPay.connect({
      locationId: session.locationId,
      tosAcceptancePermitted: opts.canAcceptTerms,
      merchantDisplayName: opts.merchantDisplayName,
    });
  } finally {
    primedToken = null;
  }
}

/**
 * Apple requirement 1.5 — warm Tap to Pay up at launch and on foreground, so
 * that 5.6 (the reader UI on screen inside one second, ≥90% of the time) is
 * achievable at all. A cold sheet has to fetch a connection token, discover the
 * reader, connect it and confirm Apple's account link before it can show
 * anything; done here, that has already happened by the time somebody opens a
 * job.
 *
 * 🚨 Only reaches native at all on a device that has used Tap to Pay before
 * (`hasUsedTapToPay`). Checking `termsAccepted` is not enough on its own:
 * reading it starts Stripe's SDK on the main thread, and doing that on every
 * launch for every iOS user froze the app for seconds (MUCKA-IOS-S).
 *
 * 🚨 Then only warms up when terms are ALREADY accepted, and that is what keeps
 * it from being a silent API call on every resume for every user in the
 * country. `available()` excludes web, Android and any binary without the
 * plugin; `termsAccepted` is read from Apple (1.6, never cached) and can only
 * be true if an owner went through Settings → Payments, which is itself behind
 * the launch flag. So this self-gates on the flag without needing to see it.
 *
 * `canAcceptTerms: false` deliberately: a warm-up must never be able to raise
 * Apple's terms sheet at a moment nobody asked for one. Terms are accepted in
 * exactly one place, by an owner who pressed a button.
 *
 * Best-effort throughout — a phone with no signal on resume is not an error
 * anybody needs to hear about, it just means the next sheet pays the cost.
 */
export async function warmUpTapToPay(): Promise<void> {
  if (!available() || !hasUsedTapToPay()) return;
  try {
    const status = await getTapToPayStatus();
    if (!status.termsAccepted || status.connected) return;
    await connectTapToPay({ canAcceptTerms: false });
  } catch {
    // Nothing to report and nothing to retry — the sheet connects on demand.
  }
}

/**
 * Take one payment against one invoice.
 *
 * 🚨 The amount is NOT a parameter and must never become one. The server
 * computes it from the invoice and bakes it into the PaymentIntent; this
 * function only ever handles the resulting client secret. A device that could
 * name the figure could settle a £4,000 invoice with a £1 tap.
 *
 * ⚠️ Resolving does NOT mean the invoice is paid in Mucka. The books are
 * written by the Stripe webhook, which is the only thing that has watched the
 * money move. Treat this as "the card was accepted" and let the invoice update
 * arrive on its own — a phone that loses signal between the tap and here must
 * not be able to mark an invoice settled.
 */
export async function collectTapToPayPayment(invoiceId: string): Promise<TapToPayResult> {
  if (!available()) throw new Error('Tap to Pay on iPhone is not available on this device.');

  const res = await fetch('/api/terminal/payment-intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ invoiceId }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    // 409 bodies are meant to be read out ("already paid", "too small to take
    // by card", "card payments aren't switched on"). Keep them.
    throw new Error(body?.error || 'Could not start the payment.');
  }

  return MuckaTapToPay.collectPayment({ clientSecret: body.clientSecret });
}

/** Back out of a card read. Only possible before the payment is confirmed. */
export async function cancelTapToPayPayment(): Promise<void> {
  if (!available()) return;
  await MuckaTapToPay.cancelPayment().catch(() => {});
}

/** Apple requirements 5.7 and 5.8 — the initializing and processing screens. */
export function onTapToPayStage(
  fn: (data: { stage: TapToPayStage }) => void,
): Promise<PluginListenerHandle | null> {
  if (!available()) return Promise.resolve(null);
  return MuckaTapToPay.addListener('paymentStage', fn).catch(() => null);
}

/** Where the education came from, so the caller knows whether to show ours. */
export type EducationResult = 'shown' | 'needs_fallback';

/**
 * Show Apple's own "How to Tap" walkthrough.
 *
 * Apple requirement 4.1: using `ProximityReaderDiscovery` satisfies 4.4, 4.6,
 * 4.7 and 4.8 in one call, and Apple keep it current and localised. Far better
 * than four screens of ours that quietly go stale.
 *
 * 🚨 Returns `needs_fallback` rather than throwing when Apple cannot show it,
 * and the caller MUST honour that. It is iOS 18+, which is higher than Tap to
 * Pay's own 16.4 floor, so there is a real band of phones that can take a
 * payment but cannot see Apple's guide — plus transient cases like no network.
 * Requirement 4.2 still applies to those users, so they get our help article.
 */
export async function showTapToPayEducation(): Promise<EducationResult> {
  if (!available()) return 'needs_fallback';
  try {
    const res = await MuckaTapToPay.showEducation();
    return res?.shown ? 'shown' : 'needs_fallback';
  } catch {
    return 'needs_fallback';
  }
}

/** Configuration progress, for the indicator Apple require at 3.9.1. */
export function onTapToPayProgress(
  fn: (data: { progress: number; installing: boolean; error?: string | null }) => void,
): Promise<PluginListenerHandle | null> {
  if (!available()) return Promise.resolve(null);
  return MuckaTapToPay.addListener('updateProgress', fn).catch(() => null);
}

/**
 * "Present card" / "Remove card" and friends, already localised by Stripe.
 * Apple want these shown as given, so pass them through rather than writing
 * our own wording.
 */
export function onTapToPayReaderMessage(
  fn: (data: { message: string; code: number }) => void,
): Promise<PluginListenerHandle | null> {
  if (!available()) return Promise.resolve(null);
  return MuckaTapToPay.addListener('displayMessage', fn).catch(() => null);
}
